import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { 
  hasGoogleAvatar, 
  normalizeGoogleAvatarUrl, 
  getAvatarPath, 
  generateDeterministicAvatarId 
} from '@/utils/avatars';
import { getUserProfile, getAnonymousUserProfile, type UserProfile } from '@/services/profileService';

interface UserAvatarInfo {
  url: string;
  type: 'google' | 'custom' | 'uploaded' | 'default';
  isLoading: boolean;
}

/**
 * Hook to get the current user's avatar with proper priority:
 * 1. Custom uploaded image (if set)
 * 2. Custom avatar from profile (if set)
 * 3. Google avatar (for Google users and useGoogleAvatar is true)
 * 4. Deterministic default avatar
 */
export function useUserAvatar(user: User | null, refreshTrigger?: number): UserAvatarInfo {
  const [avatarInfo, setAvatarInfo] = useState<UserAvatarInfo>({
    url: '',
    type: 'default',
    isLoading: true,
  });

  useEffect(() => {
    const loadUserAvatar = async () => {
      if (!user) {
        setAvatarInfo({
          url: '',
          type: 'default',
          isLoading: false,
        });
        return;
      }

      setAvatarInfo(prev => ({ ...prev, isLoading: true }));

      try {
        let profile: Partial<UserProfile> | null = null;

        // Get profile data
        if (user.isAnonymous) {
          profile = getAnonymousUserProfile(user.uid);
        } else {
          profile = await getUserProfile(user.uid);
        }

        // Priority 1: Custom uploaded image
        if (profile?.customAvatarUrl) {
          setAvatarInfo({
            url: profile.customAvatarUrl,
            type: 'uploaded',
            isLoading: false,
          });
          return;
        }

        // Priority 2: Google avatar (for Google users or explicitly requested)

        // For Google users, use Google avatar by default if:
        // 1. Profile explicitly sets useGoogleAvatar to true, OR
        // 2. Profile doesn't exist yet (new Google user), OR
        // 3. Profile exists but useGoogleAvatar is undefined (not explicitly set to false) for Google users
        const isGoogleUser = user?.providerData?.[0]?.providerId === 'google.com';
        const shouldUseGoogleAvatar = hasGoogleAvatar(user) && (
          profile?.useGoogleAvatar === true ||
          (!profile && isGoogleUser) ||
          (profile && profile.useGoogleAvatar === undefined && isGoogleUser)
        );

        if (shouldUseGoogleAvatar) {

          setAvatarInfo({
            url: normalizeGoogleAvatarUrl(user.photoURL!),
            type: 'google',
            isLoading: false,
          });
          return;
        }

        // Priority 3: Custom avatar from profile (random avatars) - only if not using Google avatar
        if (profile?.avatarId) {
          setAvatarInfo({
            url: getAvatarPath(profile.avatarId),
            type: 'custom',
            isLoading: false,
          });
          return;
        }

        // Priority 4: For anonymous users, generate and store a random avatar in localStorage
        if (user.isAnonymous) {
          // Generate a new random avatar and store it in localStorage
          const { generateRandomAvatarId } = await import('@/utils/avatars');
          const { updateAnonymousUserProfile } = await import('@/services/profileService');

          const newAvatarId = generateRandomAvatarId();
          const updatedProfile = { ...profile, avatarId: newAvatarId };
          updateAnonymousUserProfile(updatedProfile, user.uid);

          setAvatarInfo({
            url: getAvatarPath(newAvatarId),
            type: 'custom',
            isLoading: false,
          });
        } else {
          // For authenticated users, use deterministic default avatar
          const defaultAvatarId = generateDeterministicAvatarId(user.uid);
          setAvatarInfo({
            url: getAvatarPath(defaultAvatarId),
            type: 'default',
            isLoading: false,
          });
        }
      } catch (error) {
        console.error('Error loading user avatar:', error);
        
        // Fallback to Google avatar or default
        if (hasGoogleAvatar(user)) {
          setAvatarInfo({
            url: normalizeGoogleAvatarUrl(user.photoURL!),
            type: 'google',
            isLoading: false,
          });
        } else if (user.isAnonymous) {
          // For anonymous users, generate and store a random avatar
          try {
            const { generateRandomAvatarId } = await import('@/utils/avatars');
            const { updateAnonymousUserProfile } = await import('@/services/profileService');

            const newAvatarId = generateRandomAvatarId();
            const updatedProfile = { avatarId: newAvatarId };
            updateAnonymousUserProfile(updatedProfile, user.uid);

            setAvatarInfo({
              url: getAvatarPath(newAvatarId),
              type: 'custom',
              isLoading: false,
            });
          } catch (fallbackError) {
            console.error('Error in fallback avatar generation:', fallbackError);
            setAvatarInfo({
              url: getAvatarPath(1),
              type: 'default',
              isLoading: false,
            });
          }
        } else {
          const defaultAvatarId = generateDeterministicAvatarId(user.uid);
          setAvatarInfo({
            url: getAvatarPath(defaultAvatarId),
            type: 'default',
            isLoading: false,
          });
        }
      }
    };

    loadUserAvatar();
  }, [user, refreshTrigger]);

  return avatarInfo;
}
